// History tab body — item picker + range picker + sparkline.
// Phase 4 Step 18.
//
// Layout:
//   [item picker (vertical list)] | [range picker (horizontal row)]
//                                 | [sparkline chart fills remainder]
//
// State (selected item, selected range, last-sent dedup) lives on
// this class — same pattern as MarketNpcTab. Snapshot from MarketUI
// carries the price-history map. On state change, send() fires a
// new getPriceHistory request; Step 19 wires the response handler
// to populate `priceHistories[itemId]`.
//
// Crafted-item picker (Fix 3, Phase 4 close): crafted bases (e.g.
// "copper_wire") are sent as `itemIdPrefix`, so the server folds
// every quality-suffixed row (copper_wire:poor, copper_wire:good, …)
// into one sparkline. NPC raws keep the exact `itemId` lookup. The
// decision is static-derivable from `recipeList` at build time;
// see `CRAFTED_BASE_IDS` below.

import { Container, Graphics, Text } from "pixi.js";
import { balance } from "./types.ts";
import { getItemDef, items } from "../../shared/items.ts";
import type { ItemId } from "../../shared/items.ts";
import type { PriceHistoryEntry } from "../../shared/market.ts";
import type { ClientMessage } from "../../shared/messages.ts";
import {
  buildGetPriceHistoryMessage,
  buildGetPriceHistoryPrefixMessage,
  buildRequestRemoteMarketViewMessage,
} from "../../shared/messages.ts";
import { npcItemList } from "../../shared/npc-items.ts";
import { recipeList } from "../../shared/recipes.ts";
import { STATION_IDS, STATIONS, type StationId } from "../../shared/stations.ts";
import {
  BTN_H,
  BUTTON_PALETTE_DEFAULT,
  makeRowButton,
} from "./market-ui-widgets.ts";

const PAD = 12; // contracts:allow-literal — inner padding
const PICKER_W = 160; // contracts:allow-literal — left-column item-picker width
const PICKER_BTN_W = PICKER_W - PAD * 2; // contracts:allow-literal — symmetric L+R padding
const PICKER_BTN_H = 24; // contracts:allow-literal — picker button height
const PICKER_GAP = 4; // contracts:allow-literal — gap between picker buttons
const RANGE_BTN_W = 56; // contracts:allow-literal — range button width
const RANGE_GAP = 6; // contracts:allow-literal — gap between range buttons
const RANGE_ROW_H = BTN_H + PAD; // range row + bottom padding
const CHART_PAD_X = 20; // contracts:allow-literal — chart inset (room for y-axis labels)
const CHART_PAD_Y = 14; // contracts:allow-literal — chart vertical inset
const SPARKLINE_COLOR = 0x66aaff;
const SPARKLINE_WIDTH = 2; // contracts:allow-literal — line stroke width
const AXIS_LABEL_SIZE = 10; // contracts:allow-literal

const STATION_ROW_H = BTN_H + PAD; // station picker row height, same cadence as range row
const TABLE_BG = 0x0e0e22;
const TABLE_BORDER = 0x223366;
const PICKER_BG_ACTIVE = 0x2a2a55;
const PICKER_BG_INACTIVE = 0x1a1a3a;
const PICKER_BORDER = 0x3344aa;
const TEXT_BRIGHT = 0xeeeeff;
const TEXT_MUTED = 0x8888aa;
const TEXT_PLACEHOLDER = 0x556688;
const SWATCH_SZ = 14; // contracts:allow-literal — small swatch in picker
const PICKER_FONT_SIZE = 11; // contracts:allow-literal
const BODY_SIZE = 11; // contracts:allow-literal

// Items shown in the picker: NPC raws + crafted base IDs. Derived so catalog additions auto-appear.
const HISTORY_ITEMS: readonly ItemId[] = [
  ...npcItemList.map((c) => c.itemId),
  ...recipeList.map((r) => r.outputBaseId as ItemId),
];

// Fix 3: every recipe output is a crafted base; stored under an
// `<id>:<quality>` suffix in price_history. Picking one fires
// `itemIdPrefix` instead of `itemId` so the server LIKE-matches all
// quality rows. Kept as a Set for O(1) lookup.
const CRAFTED_BASE_IDS: ReadonlySet<string> = new Set(recipeList.map((r) => r.outputBaseId));

export type HistoryTabSnapshot = {
  priceHistories: Record<string, readonly PriceHistoryEntry[]>;
  // Phase 6 Block C: remote market data keyed as [stationId][itemKey].
  remoteHistories: Record<string, Record<string, readonly PriceHistoryEntry[]>>;
  // True when the player has market-analysis at Journeyman+ (500 XP).
  remoteMarketEnabled: boolean;
};

export type HistoryTabCallbacks = {
  send: (msg: ClientMessage) => void;
};

export class MarketHistoryTab {
  private container: Container | null = null;
  private lastParent: Container | null = null;
  private lastRect: { x: number; y: number; w: number; h: number } | null = null;
  private lastSnap: HistoryTabSnapshot | null = null;
  private lastCb: HistoryTabCallbacks | null = null;

  // Defaults: first item, 24h range, local station. Reset() returns to these.
  // HISTORY_ITEMS / priceHistoryRanges are static catalog data — index
  // access is safe by construction.
  private selectedItemId: ItemId = HISTORY_ITEMS[0] ?? "copper";
  private selectedRangeMs: number = balance.economy.priceHistoryRanges[2]?.ms ?? 24 * 60 * 60 * 1000; // contracts:allow-literal — 24h fallback
  // null = local station; non-null = remote station (Market Analysis).
  private selectedStationId: StationId | null = null;
  private lastSentItemId: ItemId | null = null;
  private lastSentRangeMs: number | null = null;
  private lastSentStationId: StationId | null = null;

  build(
    parent: Container,
    x: number,
    y: number,
    w: number,
    h: number,
    snap: HistoryTabSnapshot,
    cb: HistoryTabCallbacks,
  ): void {
    this.lastParent = parent;
    this.lastRect = { x, y, w, h };
    this.lastSnap = snap;
    this.lastCb = cb;
    this.maybeSend();
    this.render();
  }

  teardown(): void {
    if (this.container) {
      this.container.parent?.removeChild(this.container);
      this.container.destroy({ children: true });
      this.container = null;
    }
  }

  // __nexusDebug projections.
  get currentItemId(): string { return this.selectedItemId; }
  get currentRangeMs(): number { return this.selectedRangeMs; }
  get currentStationId(): StationId | null { return this.selectedStationId; }

  // Fire a price-history request when (item, range, station) changed since
  // last send. Local station → getPriceHistory; remote station →
  // requestRemoteMarketView (market-analysis Journeyman+ gate enforced server-side).
  private maybeSend(): void {
    if (!this.lastCb) return;
    if (
      this.lastSentItemId === this.selectedItemId &&
      this.lastSentRangeMs === this.selectedRangeMs &&
      this.lastSentStationId === this.selectedStationId
    ) return;
    const sinceTs = Date.now() - this.selectedRangeMs;
    let msg: ClientMessage;
    if (this.selectedStationId !== null) {
      msg = CRAFTED_BASE_IDS.has(this.selectedItemId)
        ? buildRequestRemoteMarketViewMessage(this.selectedStationId, undefined, this.selectedItemId, sinceTs)
        : buildRequestRemoteMarketViewMessage(this.selectedStationId, this.selectedItemId, undefined, sinceTs);
    } else {
      msg = CRAFTED_BASE_IDS.has(this.selectedItemId)
        ? buildGetPriceHistoryPrefixMessage(this.selectedItemId, sinceTs)
        : buildGetPriceHistoryMessage(this.selectedItemId, sinceTs);
    }
    this.lastCb.send(msg);
    this.lastSentItemId = this.selectedItemId;
    this.lastSentRangeMs = this.selectedRangeMs;
    this.lastSentStationId = this.selectedStationId;
  }

  private rebuildIfMounted(): void {
    if (this.lastParent && this.lastRect && this.lastSnap && this.lastCb) {
      this.maybeSend();
      this.render();
    }
  }

  private render(): void {
    if (!this.lastParent || !this.lastRect || !this.lastSnap) return;
    const parent = this.lastParent;
    const { x, y, w, h } = this.lastRect;
    this.teardown();
    const root = new Container();
    root.x = x;
    root.y = y;
    parent.addChild(root);
    this.container = root;

    root.addChild(
      new Graphics().rect(0, 0, w, h).fill({ color: TABLE_BG }).stroke({ color: TABLE_BORDER, width: 1 }), // contracts:allow-literal
    );

    const stationRowH = this.lastSnap.remoteMarketEnabled ? STATION_ROW_H : 0;
    this.buildItemPicker(root);
    const rightX = PICKER_W;
    const rightW = w - PICKER_W;
    if (this.lastSnap.remoteMarketEnabled) {
      this.buildStationRow(root, rightX, 0, rightW);
    }
    this.buildRangeRow(root, rightX, stationRowH, rightW);
    this.buildSparkline(root, rightX, stationRowH + RANGE_ROW_H, rightW, h - stationRowH - RANGE_ROW_H);
  }

  private buildItemPicker(parent: Container): void {
    const list = new Container();
    list.x = PAD;
    list.y = PAD;
    parent.addChild(list);
    let cy = 0;
    for (const itemId of HISTORY_ITEMS) {
      const isActive = itemId === this.selectedItemId;
      const btn = new Container();
      btn.x = 0;
      btn.y = cy;
      btn.eventMode = "static";
      btn.cursor = "pointer";

      btn.addChild(
        new Graphics()
          .roundRect(0, 0, PICKER_BTN_W, PICKER_BTN_H, 3) // contracts:allow-literal — corner radius
          .fill({ color: isActive ? PICKER_BG_ACTIVE : PICKER_BG_INACTIVE })
          .stroke({ color: PICKER_BORDER, width: 1 }), // contracts:allow-literal
      );

      const def = getItemDef(itemId) ?? items.copper;
      const swatch = new Graphics()
        .roundRect(0, 0, SWATCH_SZ, SWATCH_SZ, 2) // contracts:allow-literal — corner radius
        .fill({ color: def.color });
      swatch.x = 6; // contracts:allow-literal — left inset
      swatch.y = Math.round((PICKER_BTN_H - SWATCH_SZ) / 2); // contracts:allow-literal
      btn.addChild(swatch);

      const label = new Text({
        text: def.name,
        style: {
          fontSize: PICKER_FONT_SIZE,
          fill: isActive ? TEXT_BRIGHT : TEXT_MUTED,
          fontFamily: "monospace",
          fontWeight: isActive ? "bold" : "normal",
        },
      });
      label.x = swatch.x + SWATCH_SZ + 6; // contracts:allow-literal — gap
      label.y = Math.round((PICKER_BTN_H - label.height) / 2); // contracts:allow-literal
      btn.addChild(label);

      btn.on("pointertap", () => this.selectItem(itemId));
      list.addChild(btn);
      cy += PICKER_BTN_H + PICKER_GAP;
    }
  }

  private buildStationRow(parent: Container, x: number, y: number, w: number): void {
    const row = new Container(); row.x = x; row.y = y + PAD; parent.addChild(row);
    const lbl = new Text({ text: "Station:", style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" } });
    lbl.y = Math.round((BTN_H - lbl.height) / 2); row.addChild(lbl); // contracts:allow-literal
    let cx = lbl.width + 8; // contracts:allow-literal — gap after label
    const BTN_W = RANGE_BTN_W + 10; // contracts:allow-literal — station label is wider than range label
    const entries: Array<{ label: string; stationId: StationId | null }> = [
      { label: "Local", stationId: null },
      ...STATION_IDS.map((id) => ({ label: STATIONS[id].displayName, stationId: id as StationId | null })),
    ];
    for (const { label, stationId } of entries) {
      const isActive = this.selectedStationId === stationId;
      const btn = makeRowButton({ label, palette: BUTTON_PALETTE_DEFAULT, disabled: false, width: BTN_W, onClick: () => this.selectStation(stationId), overrideLabel: isActive ? `▸ ${label}` : undefined });
      btn.x = cx; btn.y = 0; row.addChild(btn);
      cx += BTN_W + RANGE_GAP;
    }
    void w;
  }

  private buildRangeRow(parent: Container, x: number, y: number, w: number): void {
    const row = new Container();
    row.x = x;
    row.y = y + PAD;
    parent.addChild(row);
    const lbl = new Text({
      text: "Range:",
      style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
    });
    lbl.x = 0;
    lbl.y = Math.round((BTN_H - lbl.height) / 2); // contracts:allow-literal
    row.addChild(lbl);
    let cx = lbl.width + 8; // contracts:allow-literal — gap after label
    for (const range of balance.economy.priceHistoryRanges) {
      const isActive = range.ms === this.selectedRangeMs;
      const btn = makeRowButton({
        label: range.label,
        palette: BUTTON_PALETTE_DEFAULT,
        disabled: false,
        width: RANGE_BTN_W,
        onClick: () => this.selectRange(range.ms),
        overrideLabel: isActive ? `▸ ${range.label}` : undefined,
      });
      btn.x = cx;
      btn.y = 0;
      row.addChild(btn);
      cx += RANGE_BTN_W + RANGE_GAP;
    }
    void w; // overflow handling deferred until tabs ever add a 5th range
  }

  private buildSparkline(parent: Container, x: number, y: number, w: number, h: number): void {
    const chart = new Container();
    chart.x = x;
    chart.y = y;
    parent.addChild(chart);

    const allEntries = this.selectedStationId !== null
      ? (this.lastSnap?.remoteHistories[this.selectedStationId]?.[this.selectedItemId] ?? [])
      : (this.lastSnap?.priceHistories[this.selectedItemId] ?? []);
    const sinceTs = Date.now() - this.selectedRangeMs;
    const entries = allEntries.filter((e) => e.ts >= sinceTs);

    if (entries.length === 0) {
      const empty = new Text({
        text: "no price data in this range",
        style: { fontSize: BODY_SIZE, fill: TEXT_PLACEHOLDER, fontFamily: "monospace", fontStyle: "italic" },
      });
      empty.x = Math.round((w - empty.width) / 2); // contracts:allow-literal — centering idiom
      empty.y = Math.round((h - empty.height) / 2); // contracts:allow-literal — centering idiom
      chart.addChild(empty);
      return;
    }

    // Y-bounds. Single-point case becomes a flat line; expand the
    // y-range artificially so the line doesn't sit on the bottom edge.
    let minPrice = Infinity;
    let maxPrice = -Infinity;
    for (const e of entries) {
      if (e.price < minPrice) minPrice = e.price;
      if (e.price > maxPrice) maxPrice = e.price;
    }
    if (minPrice === maxPrice) {
      maxPrice = minPrice + 1; // contracts:allow-literal — flat-line buffer
    }

    const chartLeft = CHART_PAD_X;
    const chartTop = CHART_PAD_Y;
    const chartW = w - CHART_PAD_X * 2; // contracts:allow-literal — symmetric L+R inset
    const chartH = h - CHART_PAD_Y * 2; // contracts:allow-literal — symmetric T+B inset
    const maxTs = Date.now();
    const minTs = sinceTs;
    const xOf = (ts: number): number =>
      chartLeft + ((ts - minTs) / (maxTs - minTs)) * chartW;
    const yOf = (price: number): number =>
      chartTop + chartH - ((price - minPrice) / (maxPrice - minPrice)) * chartH;

    // Sparkline. Single point ↦ short flat segment so it's visible.
    const line = new Graphics();
    const sorted = [...entries].sort((a, b) => a.ts - b.ts);
    const first = sorted[0];
    if (first === undefined) return; // unreachable — entries.length>0 guards above
    if (sorted.length === 1) {
      const yy = yOf(first.price);
      line.moveTo(chartLeft, yy).lineTo(chartLeft + chartW, yy);
    } else {
      line.moveTo(xOf(first.ts), yOf(first.price));
      for (let i = 1; i < sorted.length; i++) {
        const e = sorted[i];
        if (e === undefined) continue;
        line.lineTo(xOf(e.ts), yOf(e.price));
      }
    }
    line.stroke({ color: SPARKLINE_COLOR, width: SPARKLINE_WIDTH });
    chart.addChild(line);

    // Y-axis min/max labels (right-aligned, on the chart's left edge).
    const maxLabel = new Text({
      text: `\u20B5 ${maxPrice}`,
      style: { fontSize: AXIS_LABEL_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
    });
    maxLabel.x = chartLeft - maxLabel.width - 4; // contracts:allow-literal — gap from chart
    maxLabel.y = chartTop;
    chart.addChild(maxLabel);

    const minLabel = new Text({
      text: `\u20B5 ${minPrice}`,
      style: { fontSize: AXIS_LABEL_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
    });
    minLabel.x = chartLeft - minLabel.width - 4; // contracts:allow-literal — gap from chart
    minLabel.y = chartTop + chartH - minLabel.height;
    chart.addChild(minLabel);
  }

  private selectItem(itemId: ItemId): void {
    if (this.selectedItemId === itemId) return;
    this.selectedItemId = itemId;
    this.rebuildIfMounted();
  }

  private selectRange(ms: number): void {
    if (this.selectedRangeMs === ms) return;
    this.selectedRangeMs = ms;
    this.rebuildIfMounted();
  }

  private selectStation(stationId: StationId | null): void {
    if (this.selectedStationId === stationId) return;
    this.selectedStationId = stationId;
    this.rebuildIfMounted();
  }
}
